/**
 * Señal única de «esta cuenta ha dejado de estar activa» (#559).
 *
 * El corte de una cuenta llega al cliente por DOS canales distintos, y hasta ahora solo uno
 * llevaba al usuario a la pantalla explicativa:
 *
 *   1. HTTP  → `apiClient` recibe 403 con `code` ACCOUNT_SUSPENDED/ACCOUNT_BLOCKED y dispara
 *              el CustomEvent `auth:account-inactive`. Esto ya funcionaba.
 *   2. WebSocket → el backend hace `emit('account-inactive', { reason })` a cada socket del
 *              usuario antes de cerrarlo (`socketManager.disconnectUser`), y rechaza el
 *              handshake con `next(new Error(code))`, que en el cliente llega como
 *              `connect_error` con ese código en `err.message`. **Nadie lo escuchaba**: el
 *              usuario con una pestaña abierta veía el spinner genérico de reconexión, que es
 *              justo lo que el comentario del emisor dice estar evitando.
 *
 * Este módulo es el punto único que traduce el canal 2 al mismo CustomEvent del canal 1, para
 * que `<AccountStatusListener />` reaccione sin que a los hooks de socket les importe adónde
 * navega la app. `apiClient` sigue disparando el evento por su cuenta a propósito: tocar su
 * camino —el que ya funciona— para refactorizarlo aquí sería riesgo sin beneficio.
 */

/** Los dos códigos que el backend usa para una cuenta cortada (`INACTIVE_CODES` allí). */
const ACCOUNT_INACTIVE_CODES = ['ACCOUNT_SUSPENDED', 'ACCOUNT_BLOCKED'];

/**
 * ¿Este mensaje de error de socket es un corte de cuenta?
 *
 * Se compara con el código EXACTO, nunca con `includes`: el handshake manda el código a secas
 * (`next(new Error('ACCOUNT_SUSPENDED'))`), y un `includes` acabaría capturando mensajes de red
 * ajenos que casualmente contuvieran la palabra.
 *
 * @param {unknown} value - `err.message` de un `connect_error`, o lo que sea.
 * @returns {boolean}
 */
export function isAccountInactiveCode(value) {
  return typeof value === 'string' && ACCOUNT_INACTIVE_CODES.includes(value);
}

/**
 * Anuncia al SPA que la cuenta no está activa. Idempotente en la práctica: si ya estamos en la
 * pantalla, no dispara nada, y por eso los reintentos de socket.io —que en `useUserSocket` son
 * infinitos— no producen una cascada de eventos.
 *
 * @param {{ code?: string, source: string }} detail - `code` es el del backend cuando se
 *   conoce; `source` identifica el hook que lo detectó, y solo se usa para trazas.
 * @returns {void}
 */
export function signalAccountInactive(detail) {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/account/inactive') return;

  window.dispatchEvent(new CustomEvent('auth:account-inactive', {
    detail: {
      code: detail?.code || 'ACCOUNT_INACTIVE',
      source: detail?.source,
      timestamp: Date.now(),
    },
  }));
}

/**
 * Traduce el `reason` del evento de socket (`account_suspended`) al código del backend
 * (`ACCOUNT_SUSPENDED`). Coinciden salvo en la caja, así que no hace falta un mapa que
 * mantener sincronizado.
 *
 * @param {unknown} reason
 * @returns {string|undefined}
 */
export function codeFromSocketReason(reason) {
  if (typeof reason !== 'string' || reason.length === 0) return undefined;
  const code = reason.toUpperCase();
  return ACCOUNT_INACTIVE_CODES.includes(code) ? code : undefined;
}
